import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { api } from '../../services/api';
import { MediaResponse } from '../../types';

interface MediaTabProps {
  garage: { id: string };
  token: string;
}

export default function MediaTab({ garage, token }: MediaTabProps) {
  const { t } = useLanguage();
  const [media, setMedia] = useState<MediaResponse[]>([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [reordering, setReordering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One request instead of the old 1 + N sequential /download-url calls: the media list
  // already carries presigned URLs, and it is the only place mediaId is exposed — which the
  // reorder below needs.
  const loadMedia = useCallback(async () => {
    if (!token) return;
    try {
      setMedia(await api.getMyGarageMedia(token));
      setError(null);
    } catch {
      setError(t('profile.media.loadFailed'));
    }
  }, [token, t]);

  useEffect(() => { loadMedia(); }, [loadMedia, garage.id]);

  const logo = media.find((m) => m.mediaType === 'LOGO');

  // Deduplicate by storageKey — historical rows can point at the same object.
  const gallery = (() => {
    const seen = new Set<string>();
    return media
      .filter((m) => m.mediaType === 'GALLERY_PHOTO')
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .filter((m) => { if (seen.has(m.storageKey)) return false; seen.add(m.storageKey); return true; });
  })();

  const move = async (index: number, delta: number) => {
    const to = index + delta;
    if (to < 0 || to >= gallery.length || reordering) return;
    const next = [...gallery];
    [next[index], next[to]] = [next[to], next[index]];

    const previous = media;
    // Optimistic, so holding the arrow doesn't fight the round trip.
    setMedia([...media.filter((m) => m.mediaType !== 'GALLERY_PHOTO'),
      ...next.map((m, i) => ({ ...m, sortOrder: i }))]);
    if (selectedImage === index) setSelectedImage(to);
    else if (selectedImage === to) setSelectedImage(index);

    setReordering(true);
    try {
      setMedia(await api.reorderGarageMedia(next.map((m) => m.id), token));
      setError(null);
    } catch {
      setMedia(previous);
      setError(t('profile.media.reorderFailed'));
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="profile-tab-content">
      {error && <div className="profile-inline-error">{error}</div>}

      <section className="profile-section">
        <h3 className="profile-section-title">{t('profile.media.logo')}</h3>
        {logo?.downloadUrl ? (
          <div className="media-logo">
            <img src={logo.downloadUrl} alt="Garage Logo" />
          </div>
        ) : (
          <p className="media-empty">{t('profile.media.noLogo')}</p>
        )}
      </section>

      <section className="profile-section">
        <h3 className="profile-section-title">{t('profile.media.gallery')}</h3>
        {gallery.length > 0 ? (
          <div className="media-gallery">
            <div className="media-gallery-main">
              <img src={(gallery[selectedImage] || gallery[0]).downloadUrl ?? undefined} alt="Garage" />
            </div>
            <div className="media-gallery-thumbnails">
              {gallery.map((m, index) => (
                <div
                  key={m.id}
                  className={`media-thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                >
                  <img src={m.downloadUrl ?? undefined} alt={`Gallery ${index + 1}`} />
                  {gallery.length > 1 && (
                    <div className="v2-photo-tile-order">
                      <button
                        type="button"
                        className="v2-photo-move"
                        disabled={index === 0 || reordering}
                        aria-label={`Move photo ${index + 1} earlier`}
                        title={t('services.photos.moveLeft')}
                        onClick={(e) => { e.stopPropagation(); move(index, -1); }}
                      >‹</button>
                      <span className="v2-photo-tile-position">{index + 1}</span>
                      <button
                        type="button"
                        className="v2-photo-move"
                        disabled={index === gallery.length - 1 || reordering}
                        aria-label={`Move photo ${index + 1} later`}
                        title={t('services.photos.moveRight')}
                        onClick={(e) => { e.stopPropagation(); move(index, 1); }}
                      >›</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {gallery.length > 1 && (
              <p className="v2-photos-order-hint">{t('profile.media.orderHint')}</p>
            )}
          </div>
        ) : (
          <p className="media-empty">{t('profile.media.noPhotos')}</p>
        )}
      </section>
    </div>
  );
}
